import { useState, useEffect } from "react";
import { getTransactions } from "@/lib/api";
import { naira } from "@/components/PricingSection";
import { TopUpPackages } from "@/components/TopUpPackages";
import { Loader2 } from "lucide-react";
import { logWarn } from "@/lib/log";

const TransactionRows = ({ items }) => {
    if (items === null) {
        return (
            <div className="p-10 flex justify-center">
                <Loader2 className="w-5 h-5 text-blaze animate-spin" />
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <div className="p-10 font-mono text-xs text-white/30 text-center" data-testid="billing-empty">
                No transactions yet — top up or stream to see history here.
            </div>
        );
    }
    return items.map((t) => (
        <div
            key={t.id}
            className="grid grid-cols-12 px-5 py-4 border-b border-white/5 font-mono text-xs items-center"
            data-testid={`billing-row-${t.id}`}
        >
            <div className="col-span-5 sm:col-span-4 text-white/80 pr-2">
                {t.description}
            </div>
            <div className="col-span-3 sm:col-span-3 text-white/40">
                {new Date(t.created_at).toLocaleString()}
            </div>
            <div className="col-span-2 sm:col-span-3 text-right text-white/60">
                {t.amount_ngn ? naira(t.amount_ngn) : "—"}
            </div>
            <div
                className={`col-span-2 text-right font-bold ${
                    t.credits >= 0 ? "text-green-400" : "text-blaze"
                }`}
            >
                {t.credits >= 0 ? `+${t.credits.toLocaleString()}` : t.credits.toLocaleString()}
            </div>
        </div>
    ));
};

export default function Billing() {
    const [items, setItems] = useState(null);

    useEffect(() => {
        getTransactions()
            .then(setItems)
            .catch((e) => {
                logWarn("transactions load failed", e);
                setItems([]);
            });
    }, []);

    return (
        <div className="max-w-5xl" data-testid="billing-page">
            <div className="mb-8">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">/ Billing</div>
                <h1 className="font-display font-bold uppercase text-3xl mt-2 tracking-tight">Buy Credits</h1>
            </div>

            <div className="mb-10">
                <TopUpPackages />
            </div>

            <h2 className="font-display font-bold uppercase text-lg mb-4 tracking-tight">Transaction History</h2>

            <div className="border border-white/10 bg-surface">
                <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                    <div className="col-span-5 sm:col-span-4">Description</div>
                    <div className="col-span-3 sm:col-span-3">Date</div>
                    <div className="col-span-2 sm:col-span-3 text-right">Amount</div>
                    <div className="col-span-2 text-right">Credits</div>
                </div>
                <TransactionRows items={items} />
            </div>
        </div>
    );
}
